import { useCallback, useEffect, useRef, useState } from "react";
import { SourceDto, SpellDto, TranslatedText } from "../../../Types/ApiTypes";
import SpellModal from "./SpellModal";
import { useTranslation } from "react-i18next";
import { distance } from "../../../Types/TableTypes";
import SpellRowItemComponent from "./SpellRowItemComponent";
import { getTranslation } from "../../../Utils/Converters";

interface SpellTableComponentProps{
    allSpells: SpellDto[],
    allSources: SourceDto[],
    setAllSpells: (allSpells: SpellDto[]) => void
}

const SpellTableComponent = (props: SpellTableComponentProps) => {
    const [sortAttribute, setSortAttribute] = useState("id");
    const [sortDirection, setSortDirection] = useState("ASC");
    const [spells, setSpells] = useState(props.allSpells);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameSearchValue, setNameSearchValue] = useState("");
    const [sourceFilterValue, setSourceFilterValue] = useState([] as SourceDto[]);
    const [timeFilterValue, setTimeFilterValue] = useState([] as string[]);
    const [sourceFilterOpen, setSourceFilterOpen] = useState(false);
    const [timeFilterOpen, setTimeFilterOpen] = useState(false);
    const [concFilterOpen, setConcFilterOpen] = useState(false);
    const [concFilterValue, setConcFilterValue] = useState([] as boolean[])
    const [ritualFilterOpen, setRitualFilterOpen] = useState(false);
    const [ritualFilterValue, setRitualFilterValue] = useState([] as boolean[])
    const [timeFilters, setTimeFilters] = useState([] as string[]);
    const [classFilters, setClassFilters] = useState([] as string[]);
    const backgroundaRef = useRef<HTMLDivElement>(null);

    const { t } = useTranslation();
    const emptySpell = {
        index: "",
        source: {} as SourceDto,
        description: {} as TranslatedText,
        higherLevel: {} as TranslatedText,
        name: {} as TranslatedText,
        range: {} as distance,
        otherRange: {} as TranslatedText,
        components: {
            "v": false,
            "s": false,
            "m": false
        },
        materials: {} as TranslatedText,
        ritual: false,
        duration: {} as TranslatedText,
        concentration: false,
        castingTime: {} as TranslatedText,
        level: 0,
        attackType: {} as TranslatedText,
        damageType: {} as TranslatedText,
        dcType: {} as TranslatedText,
        dcSuccess: {} as TranslatedText,
        areaOfEffectType: {} as TranslatedText,
        areaOfEffectSize: {} as distance,
        school: {} as TranslatedText,
        classes: {} as TranslatedText,
        subclasses: {} as TranslatedText,
        // url: "",
        // updated_at: ""
    }

    const filterAndSort = useCallback(() =>{
        let filteredSpells = props.allSpells;
        // console.log("sort "+sortAttribute+" "+sortDirection)
        let times = [] as string [];
        let classes = [] as string [];

        filteredSpells.forEach((spell) => {
            if (!times.includes(getTranslation(spell.castingTime))) 
                times.push(getTranslation(spell.castingTime))
            if (!classes.includes(getTranslation(spell.classes))) 
                classes.push(getTranslation(spell.classes))
        })

        setTimeFilters(times);
        setClassFilters(classes);

        //filter ritual
        if (ritualFilterValue.length > 0)
            filteredSpells = filteredSpells.filter((spell) => {
                return ritualFilterValue.some(ritual => {
                    return spell.ritual !== undefined && ritual === spell.ritual
            });
        });

        //filter concentration
        if (concFilterValue.length > 0)
            filteredSpells = filteredSpells.filter((spell) => {
                return concFilterValue.some(conc => {
                    return spell.concentration !== undefined && conc === spell.concentration
            });
        });

        //filter sources
        if (sourceFilterValue.length > 0)
            filteredSpells = filteredSpells.filter((spell) => {
                return spell.source ? sourceFilterValue.some(source => {
                    return source.code === (spell.source? spell.source.code : undefined)
            }) : false;
        });

        //filter casting time
        if (timeFilterValue.length > 0)
            filteredSpells = filteredSpells.filter((spell) => {
                return spell.castingTime ? timeFilterValue.some(time => {
                    return time === getTranslation(spell.castingTime)
            }) : false;
        });

        //search name
        if (nameSearchValue.length > 0){
            filteredSpells = filteredSpells.filter((spell: SpellDto) => getTranslation(spell.name).toLowerCase().includes(nameSearchValue.toLowerCase()));
        }
        //sort by id
        if (sortAttribute === "id"){
            filteredSpells.sort((a, b) => sortNumbers(a.id!, b.id!, sortDirection));
        }
        //sort by index
        if (sortAttribute === "index"){
            filteredSpells.sort((a, b) => sortStrings(a.index, b.index, sortDirection));
        }
        //sort by source
        if (sortAttribute === "source"){
            filteredSpells.sort((a, b) => sortStrings(a.source? a.source.code : "", b.source ? b.source.code : "", sortDirection));
        }
        //sort by name
        if (sortAttribute === "name"){
            filteredSpells.sort((a, b) => sortStrings(getTranslation(a.name), getTranslation(b.name), sortDirection));
        }
        //sort by school
        if (sortAttribute === "school"){
            filteredSpells.sort((a, b) => sortStrings(getTranslation(a.school), getTranslation(b.school), sortDirection));
        }

        
        setSpells([...filteredSpells]);        
    },[props.allSpells, sortAttribute, sortDirection, nameSearchValue, sourceFilterValue,timeFilterValue, concFilterValue, ritualFilterValue]);


    useEffect(() => {filterAndSort()},[filterAndSort])
    function deleteSpell(id?: number){
        props.setAllSpells(props.allSpells.filter((spell: SpellDto) => spell.id !== id))
    }

    function sortNumbers(a: number, b: number, direction: string){
        return direction === "ASC" ? (a - b) : (b - a);
    }

    function sortStrings(a: string, b: string, direction: string){
        if (a > b)
            return direction === "ASC" ? 1 : -1;
        if (a < b)
            return direction === "ASC" ? -1 : 1;
        return 0;
    }

    return (
        <table className='table table-responsive text-start'>
            <thead style={{top: "80px", zIndex: "2"}}>
                <tr>
                    <th scope="col" className="">
                        <span className="d-flex">
                            <span className="d-flex "
                                role="button" onClick={(e) => {
                                    if (sortAttribute !== "id"){
                                        setSortAttribute("id");
                                        setSortDirection("ASC");
                                    }else{
                                        if(sortDirection === "ASC")
                                            setSortDirection("DESC");
                                        else
                                            setSortDirection("ASC");
                                    }
                                }}>
                                <span className="d-flex">
                                    {t("spelllist-id")}
                                    &nbsp;
                                    {sortAttribute !== "id" && <i className="bi bi-sort-up text-secondary"></i>}
                                    {sortAttribute === "id" && sortDirection === "ASC" && <i className="bi bi-sort-up"></i>}
                                    {sortAttribute === "id" && sortDirection === "DESC" && <i className="bi bi-sort-down"></i>}
                                </span>
                            </span>
                            <span>&nbsp;</span>
                        </span>
                    </th>
                    <th scope="col" className="">
                        <span className="d-flex">
                            <span className="d-flex " role="button" onClick={(e) => {
                                    if (sortAttribute !== "source"){
                                        setSortAttribute("source");
                                        setSortDirection("ASC");
                                    }else{
                                        if(sortDirection === "ASC")
                                            setSortDirection("DESC");
                                        else
                                            setSortDirection("ASC");
                                    }
                                }}>
                                
                                <span>{t("spelllist-source")}</span>
                                &nbsp;
                                {sortAttribute !== "source" && <i className="bi bi-sort-up text-secondary"></i>}
                                {sortAttribute === "source" && sortDirection === "ASC" && <i className="bi bi-sort-up"></i>}
                                {sortAttribute === "source" && sortDirection === "DESC" && <i className="bi bi-sort-down"></i>}
                            </span>
                            &nbsp;
                            <span role="button" onClick={() => setSourceFilterOpen(!sourceFilterOpen)}><i className="bi bi-funnel"></i></span>
                            {sourceFilterOpen && 
                                <>
                                    <div ref={backgroundaRef}
                                        style={{display: "block", opacity: "1", backgroundColor:"#00000000" ,width: "100%", height: "100%", position: "fixed", top: "0", left: "0"}} 
                                        tabIndex={-1} role="dialog" aria-hidden="true" 
                                        onClick={e=>{
                                            if(e.target === backgroundaRef.current)
                                            {
                                                setSourceFilterOpen(false)
                                            }
                                        }}>
                                    </div>
                                
                                    <span className="d-flex flex-column fs-5 p-4 dropdown-menu" style={{marginTop: "42px", fontWeight: "normal"}}>
                                        {
                                            props.allSources.map((source) => {
                                                return (
                                                    <label key={source.id}>
                                                        <input type="checkbox" className="form-check-input"
                                                            checked={sourceFilterValue.filter((v) => {return v.code === source.code}).length>0}
                                                            onChange={(e) => {
                                                                let value = sourceFilterValue;
                                                                if (e.target.checked){
                                                                    value.push(source);
                                                                }else{
                                                                    value = value.filter((v) => {
                                                                        return v.code !== source.code;
                                                                    });
                                                                }
                                                                
                                                                setSourceFilterValue([...value]);
                                                            }}
                                                        ></input> {getTranslation(source.name)}
                                                    </label>
                                                )
                                            })
                                        }
                                    </span>
                                </>
                            }
                        </span>
                    </th>
                    {/* <th scope="col">
                        <span className="d-flex ">
                            <span className="d-flex"
                                role="button" onClick={(e) => {
                                    if (sortAttribute !== "index"){
                                        setSortAttribute("index");
                                        setSortDirection("ASC");
                                    }else{
                                        if (sortDirection === "ASC")
                                            setSortDirection("DESC");
                                        else                                                
                                            setSortDirection("ASC");
                                    }
                                }}>

                                {t("spelllist-index")}
                                &nbsp;
                                {sortAttribute !== "index" && <i className="bi bi-sort-up text-secondary"></i>}
                                {sortAttribute === "index" && sortDirection === "ASC" && <i className="bi bi-sort-up"></i>}
                                {sortAttribute === "index" && sortDirection === "DESC" && <i className="bi bi-sort-down"></i>}
                            </span>
                            <span>&nbsp;</span>
                        </span>
                    </th> */}
                    <th scope="col" className="">
                        <span className="d-flex ">
                            <span className="d-flex"
                                role="button" onClick={(e) => {
                                    if (sortAttribute !== "name"){
                                        setSortAttribute("name");
                                        setSortDirection("ASC");
                                    }else{
                                        if (sortDirection === "ASC")
                                            setSortDirection("DESC");
                                        else
                                            setSortDirection("ASC");
                                    }
                                }}>

                                <span>{t("spelllist-name")}</span>
                                &nbsp;
                                {sortAttribute !== "name" && <i className="bi bi-sort-up text-secondary"></i>}
                                {sortAttribute === "name" && sortDirection === "ASC" && <i className="bi bi-sort-up"></i>}
                                {sortAttribute === "name" && sortDirection === "DESC" && <i className="bi bi-sort-down"></i>}
                            </span>
                            &nbsp;
                            <input placeholder={t("spell-search")} className="form-control" value={nameSearchValue}
                                onChange={(e) => {setNameSearchValue (e.target.value);}}
                            />
                         </span>
                    </th>
                    <th scope="col" className="">
                        <span className="d-flex ">
                            <span className="d-flex"
                                role="button" onClick={(e) => {
                                    if (sortAttribute !== "school"){
                                        setSortAttribute("school");
                                        setSortDirection("ASC");
                                    }else{
                                        if(sortDirection === "ASC")
                                            setSortDirection("DESC");
                                        else
                                            setSortDirection("ASC");
                                    }
                                }}>

                                <span>{t("spelllist-school")}</span>
                                &nbsp;
                                {sortAttribute !== "school" && <i className="bi bi-sort-up text-secondary"></i>}
                                {sortAttribute === "school" && sortDirection === "ASC" && <i className="bi bi-sort-up"></i>}
                                {sortAttribute === "school" && sortDirection === "DESC" && <i className="bi bi-sort-down"></i>}
                            </span>
                            <span>&nbsp;</span>
                        </span>
                    </th>
                    <th scope="col" className="">
                        <span className="d-flex ">
                            <span className="d-flex">
                                {t("spelllist-ritual")}
                            </span>
                            <span>&nbsp;</span>
                            <span role="button" onClick={() => setRitualFilterOpen(!ritualFilterOpen)}><i className="bi bi-funnel"></i></span>
                            {ritualFilterOpen && 
                                <>
                                    <div ref={backgroundaRef}
                                        style={{display: "block", opacity: "1", backgroundColor:"#00000000" ,width: "100%", height: "100%", position: "fixed", top: "0", left: "0"}} 
                                        tabIndex={-1} role="dialog" aria-hidden="true" 
                                        onClick={e=>{
                                            if(e.target === backgroundaRef.current)
                                            {
                                                setRitualFilterOpen(false)
                                            }
                                        }}>
                                    </div>
                                
                                    <span className="d-flex flex-column fs-5 p-4 dropdown-menu" style={{marginTop: "42px", fontWeight: "normal"}}>
                                        
                                        <label>
                                            <input type="checkbox" className="form-check-input"
                                                checked={ritualFilterValue.filter((v) => {return v === true}).length>0}
                                                onChange={(e) => {
                                                    let value = ritualFilterValue;
                                                    if (e.target.checked){
                                                        value.push(true);
                                                    }else{
                                                        value = value.filter((v) => {
                                                            return v !== true;
                                                        });
                                                    }
                                                    
                                                    setRitualFilterValue([...value]);
                                                }}
                                            ></input> {"true"}
                                        </label>
                                        <label>
                                            <input type="checkbox" className="form-check-input"
                                                checked={ritualFilterValue.filter((v) => {return v === false}).length>0}
                                                onChange={(e) => {
                                                    let value = ritualFilterValue;
                                                    if (e.target.checked){
                                                        value.push(false);
                                                    }else{
                                                        value = value.filter((v) => {
                                                            return v !== false;
                                                        });
                                                    }
                                                    
                                                    setRitualFilterValue([...value]);
                                                }}
                                            ></input> {"false"}
                                        </label>
                                    </span>
                                </>
                            }
                        </span>
                    </th>
                    <th scope="col" className="">
                        <span className="d-flex ">
                            <span className="d-flex">
                                {t("spelllist-concentration")}
                            </span>
                            <span>&nbsp;</span>
                            <span role="button" onClick={() => setConcFilterOpen(!concFilterOpen)}><i className="bi bi-funnel"></i></span>
                            {concFilterOpen && 
                                <>
                                    <div ref={backgroundaRef}
                                        style={{display: "block", opacity: "1", backgroundColor:"#00000000" ,width: "100%", height: "100%", position: "fixed", top: "0", left: "0"}} 
                                        tabIndex={-1} role="dialog" aria-hidden="true" 
                                        onClick={e=>{
                                            if(e.target === backgroundaRef.current)
                                            {
                                                setConcFilterOpen(false)
                                            }
                                        }}>
                                    </div>
                                
                                    <span className="d-flex flex-column fs-5 p-4 dropdown-menu" style={{marginTop: "42px", fontWeight: "normal"}}>
                                        
                                        <label>
                                            <input type="checkbox" className="form-check-input"
                                                checked={concFilterValue.filter((v) => {return v === true}).length>0}
                                                onChange={(e) => {
                                                    let value = concFilterValue;
                                                    if (e.target.checked){
                                                        value.push(true);
                                                    }else{
                                                        value = value.filter((v) => {
                                                            return v !== true;
                                                        });
                                                    }
                                                    
                                                    setConcFilterValue([...value]);
                                                }}
                                            ></input> {"true"}
                                        </label>
                                        <label>
                                            <input type="checkbox" className="form-check-input"
                                                checked={concFilterValue.filter((v) => {return v === false}).length>0}
                                                onChange={(e) => {
                                                    let value = concFilterValue;
                                                    if (e.target.checked){
                                                        value.push(false);
                                                    }else{
                                                        value = value.filter((v) => {
                                                            return v !== false;
                                                        });
                                                    }
                                                    
                                                    setConcFilterValue([...value]);
                                                }}
                                            ></input> {"false"}
                                        </label>
                                    </span>
                                </>
                            }
                        </span>
                    </th>
                    <th scope="col" className="">
                        <span className="d-flex ">
                            <span className="d-flex">
                                {t("spelllist-classes")}
                            </span>
                            <span>&nbsp;</span>
                        </span>
                    </th>
                    <th scope="col" className="">
                        <span className="d-flex ">
                            <span className="d-flex">
                                {t("spelllist-castingtime")}
                            </span>
                            <span>&nbsp;</span>
                            <span role="button" onClick={() => setTimeFilterOpen(!sourceFilterOpen)}><i className="bi bi-funnel"></i></span>
                            {timeFilterOpen && 
                                <>
                                    <div ref={backgroundaRef}
                                        style={{display: "block", opacity: "1", backgroundColor:"#00000000" ,width: "100%", height: "100%", position: "fixed", top: "0", left: "0"}} 
                                        tabIndex={-1} role="dialog" aria-hidden="true" 
                                        onClick={e=>{
                                            if(e.target === backgroundaRef.current)
                                            {
                                                setTimeFilterOpen(false)
                                            }
                                        }}>
                                    </div>
                                
                                    <span className="d-flex flex-column fs-5 p-4 dropdown-menu" style={{marginTop: "42px", fontWeight: "normal"}}>
                                        {
                                            timeFilters.map((time, index) => {
                                                return (
                                                    <label key={index}>
                                                        <input type="checkbox" className="form-check-input"
                                                            checked={timeFilterValue.filter((v) => {return v === time}).length>0}
                                                            onChange={(e) => {
                                                                let value = timeFilterValue;
                                                                if (e.target.checked){
                                                                    value.push(time);
                                                                }else{
                                                                    value = value.filter((v) => {
                                                                        return v !== time;
                                                                    });
                                                                }
                                                                
                                                                setTimeFilterValue([...value]);
                                                            }}
                                                        ></input> {time}
                                                    </label>
                                                )
                                            })
                                        }
                                    </span>
                                </>
                            }
                        </span>
                    </th>
                    <th><button className="btn btn-primary"
                            onClick={() => setIsModalOpen(true)}
                        >{t("table-add")}</button>
                        {isModalOpen && 
                        <SpellModal
                            spell={emptySpell}
                            updateSpell={(spell: SpellDto) => props.allSpells.push(spell)}
                            deleteSpell={deleteSpell}
                            closeModal={()=>{setIsModalOpen(false)}}
                        />}
                    </th>
                </tr>
                <tr>
                    <td colSpan={11} style={{borderWidth: "0", padding: "0"}}>
                        <div style={{ borderStyle: "solid", borderWidth: "2px"}}></div>
                    </td>
                </tr>
            </thead>
            <tbody className="">
                {spells.map((spell: SpellDto, index: number) =>(
                    <SpellRowItemComponent
                        key={spell.id}
                        spell={spell}
                        deleteSpell={deleteSpell}
                    />
                ))}
            </tbody>
        </table>
    )
}

export default SpellTableComponent;